// A worm's immunity bookkeeping: for every grid cell the worm painted, the distance
// it had travelled when it painted it (worm.distanceTravelledFromCells). Collision
// reads it to let a head overlap its own fresh trail.
//
// It used to be a plain object keyed by cell index. A worm paints a few dozen cells
// per step, so a long round built millions of entries (1.7M, 107MB, in a 2-minute
// 20-player round on a 1900 map), past a Durable Object's 128MB cap. This is the
// same map (every entry kept, same values, `undefined` for a cell never painted) in
// open-addressed typed arrays: 12 bytes per slot instead of ~60 per entry.
//
// It serializes as an empty object: clients never read another worm's stamps (the
// local collision keeps its own), and a reconnect snapshot should not carry them.
var EMPTY = -1;
// (Plain loops and an imul fallback below: core's test runner is PhantomJS, which has
// no TypedArray fill/slice or Math.imul.)
var imul = Math.imul || function (a, b) {
    var ah = (a >>> 16) & 0xffff, al = a & 0xffff, bh = (b >>> 16) & 0xffff, bl = b & 0xffff;
    return ((al * bl) + (((ah * bl + al * bh) << 16) >>> 0)) | 0;
};
function emptyKeys(n) {
    var k = new Int32Array(n);
    for (var i = 0; i < n; i++) {k[i] = EMPTY;}
    return k;
}

export function CellStamps(capacity) {
    var cap = 64;
    while (cap < (capacity || 0) * 2) {cap *= 2;}
    this.keys = emptyKeys(cap);
    this.vals = new Float64Array(cap);
    this.size = 0;
}

function slot(keys, key) {
    var mask = keys.length - 1;
    var h = imul(key, 0x9e3779b1) & mask;
    while (keys[h] !== EMPTY && keys[h] !== key) {h = (h + 1) & mask;}
    return h;
}

CellStamps.prototype.get = function (key) {
    var h = slot(this.keys, key);
    return this.keys[h] === EMPTY ? undefined : this.vals[h];
};

CellStamps.prototype.set = function (key, value) {
    var h = slot(this.keys, key);
    if (this.keys[h] === EMPTY) {
        if ((this.size + 1) * 4 > this.keys.length * 3) { // keep the load under 3/4
            this.grow();
            h = slot(this.keys, key);
        }
        this.keys[h] = key;
        this.size++;
    }
    this.vals[h] = value;
};

CellStamps.prototype.grow = function () {
    var oldKeys = this.keys, oldVals = this.vals;
    this.keys = emptyKeys(oldKeys.length * 2);
    this.vals = new Float64Array(oldKeys.length * 2);
    for (var i = 0; i < oldKeys.length; i++) {
        if (oldKeys[i] !== EMPTY) {
            var h = slot(this.keys, oldKeys[i]);
            this.keys[h] = oldKeys[i];
            this.vals[h] = oldVals[i];
        }
    }
};

CellStamps.prototype.clone = function () {
    var c = Object.create(CellStamps.prototype);
    c.keys = new Int32Array(this.keys);
    c.vals = new Float64Array(this.vals);
    c.size = this.size;
    return c;
};

CellStamps.prototype.toJSON = function () {
    return {};
};

// Readers and writers that also accept the plain-object form (a worm that arrived
// over the wire, where the stamps serialize to {}).
export function getCellStamp(stamps, cell) {
    if (!stamps) {return undefined;}
    return stamps instanceof CellStamps ? stamps.get(cell) : stamps[cell];
}

export function setCellStamp(worm, cell, value) {
    var stamps = worm.distanceTravelledFromCells;
    if (stamps instanceof CellStamps) {
        stamps.set(cell, value);
    } else {
        (worm.distanceTravelledFromCells = stamps || {})[cell] = value;
    }
}

export function cloneCellStamps(stamps) {
    if (stamps instanceof CellStamps) {return stamps.clone();}
    var out = new CellStamps();
    for (var k in stamps || {}) {
        if (Object.prototype.hasOwnProperty.call(stamps, k)) {out.set(Number(k), stamps[k]);}
    }
    return out;
}
